/**
 * AdminShell — persistent sidebar + workspace top bar for every /admin/* page.
 */
import React, { useMemo, useState } from 'react';
import { Link, useLocation } from 'react-router-dom';
import { Plus } from 'lucide-react';
import { AdminSidebar, useSidebarLeftOffset } from './AdminSidebar';
import PwaInstallBanner from './PwaInstallBanner';
import AddNewCleanDialog from './AddNewCleanDialog';
import ViewSwitch from '../crm/ViewSwitch';
import { useAuth } from '../../context/AuthContext';
import { AdminSubNav, SUBNAV_CLEANERS, SUBNAV_SETTINGS } from './AdminSubNav';
import { WorkspaceSearch } from './WorkspaceSearch';

const CRUMBS = [
  ['/admin/dashboard', 'Dashboard'], ['/admin/needs-review', 'Inbox · Needs Review'], ['/admin/communication', 'Inbox'], ['/admin/change-requests', 'Inbox · Needs Review'],
  ['/admin/customers', 'Clients'], ['/admin/clients', 'Clients'], ['/admin/quote-requests', 'Leads'], ['/admin/inquiries', 'Inbox · General Inquiries'],
  ['/admin/emails', 'Inbox · Email Activity'], ['/admin/calendar', 'Schedule'], ['/admin/schedule', 'Schedule'], ['/admin/appointments', 'Schedule'],
  ['/admin/tasks', 'Tasks'], ['/admin/ai-workspace', 'AI Workspace'],
  ['/admin/hiring', 'Team · Hiring'], ['/admin/cleaners', 'Team'], ['/admin/performance', 'Team · Performance'], ['/admin/payouts', 'Team · Payouts'],
  ['/admin/time-tracking', 'Team · Time Tracking'], ['/admin/qc-templates', 'Team · QC Templates'], ['/admin/my-guru', 'Team · My Guru'],
  ['/admin/billing', 'Billing'], ['/admin/invoices', 'Billing'], ['/admin/recurring-invoices', 'Billing'], ['/admin/sent-links', 'Billing'], ['/admin/inventory', 'Inventory'],
  ['/admin/reviews', 'Reviews'], ['/admin/faqs', 'Reviews'], ['/admin/audiences', 'Audiences'], ['/admin/analytics', 'Analytics'], ['/admin/reports', 'Analytics'],
  ['/admin/settings', 'Settings'], ['/admin/services', 'Settings · Services'], ['/admin/service-areas', 'Settings · Service Areas'],
  ['/admin/users', 'Settings · Users & Access'], ['/admin/audit-log', 'Settings · Audit Log'], ['/admin/integrations', 'Integrations'],
];
const SECTIONS = [['Team', SUBNAV_CLEANERS, ['/admin/my-guru']], ['Settings', SUBNAV_SETTINGS, ['/admin/recycle-bin', '/admin/duplicates']]];
const sectionFor = (pathname) => SECTIONS.find(([, items, extra]) =>
  [...items.map((i) => i.to), ...extra].some((p) => pathname === p || pathname.startsWith(`${p}/`)));

// Approved page headings (crm-dashboard-review-v1 businessShell/workspaceIntro copy), shown on section list pages.
const OPS = 'GLEAM GURU OPERATIONS';
const HEADINGS = [
  [['/admin/quote-requests'], 'FROM INTEREST TO FIRST CLEAN', 'Leads', 'Every conversation. A clear next step.'],
  [['/admin/customers', '/admin/clients'], OPS, 'Clients', 'The relationship, the properties, and the work in one place.'],
  [['/admin/calendar', '/admin/appointments', '/admin/schedule', '/admin/schedule/day', '/admin/schedule/changes'], 'THE WEEK, WORKING TOGETHER', 'Schedule', 'The right people. The right place. Everything in view.'],
  [['/admin/inquiries', '/admin/emails', '/admin/needs-review', '/admin/change-requests'], OPS, 'Inbox', 'Every conversation has an owner. Every follow-up has a next step.'],
  [['/admin/cleaners', '/admin/hiring', '/admin/time-tracking', '/admin/qc-templates', '/admin/performance', '/admin/payouts'], OPS, 'Team', 'The people, the standards, and the work behind every visit.'],
  [['/admin/billing', '/admin/billing/payments', '/admin/invoices', '/admin/recurring-invoices', '/admin/sent-links'], OPS, 'Billing', 'Invoices, payments and follow-through in one place.'],
  [['/admin/analytics', '/admin/analytics/expenses', '/admin/analytics/cash-flow', '/admin/analytics/services', '/admin/analytics/data-checks', '/admin/reports'], OPS, 'Analytics', 'See the whole money picture. Open the records behind it.'],
  [['/admin/inventory'], OPS, 'Inventory', 'Your products, your kits, your next supply run.'],
  [['/admin/reviews', '/admin/faqs'], OPS, 'Reviews', 'Client feedback, clear ownership, and thoughtful follow-through.'],
  [['/admin/audiences'], OPS, 'Audiences', 'Organize relationships while keeping consent visible.'],
  [['/admin/settings', '/admin/services', '/admin/service-areas', '/admin/users', '/admin/audit-log', '/admin/settings/notifications', '/admin/settings/sign-in-activity'], OPS, 'Settings', 'Keep the workspace consistent as the business grows.'],
];
const headingFor = (pathname) => HEADINGS.find(([paths]) => paths.includes(pathname.replace(/\/$/, '')));

const WorkspaceHeading = ({ eyebrow, title, subtitle }) => (
  <section className="max-w-[1400px] mx-auto px-5 sm:px-9 pt-10 pb-2" data-testid="workspace-heading">
    <p className="text-[9px] tracking-[0.18em] font-bold text-[#6f7264]">{eyebrow}</p>
    <h1 className="text-4xl sm:text-5xl lg:text-[44px] font-bold tracking-[-0.045em] text-[#20211d] mt-4" data-testid="workspace-heading-title">{title}<em className="not-italic text-[#939681] font-normal">.</em></h1>
    <p className="text-sm text-[#6f7264] mt-3">{subtitle}{title === 'Clients' && <> <Link to="/admin/recycle-bin" className="ml-3 text-xs text-[#5f6455] underline" data-testid="clients-archived-records-link">Archived records</Link></>}</p>
  </section>
);

const TopBar = () => {
  const { pathname } = useLocation();
  const { token } = useAuth();
  const [open, setOpen] = useState(false);
  const headers = useMemo(() => ({ Authorization: `Bearer ${token}` }), [token]);
  const label = (CRUMBS.find(([p]) => pathname.startsWith(p)) || [null, 'Workspace'])[1];
  return (
    <div className="h-16 sm:h-[72px] px-4 sm:px-9 border-b border-[#e7e4db] bg-[#faf9f6] flex items-center justify-between gap-3" data-testid="admin-topbar">
      <nav aria-label="Breadcrumb" className="flex items-center gap-3 text-xs min-w-0">
        <span className="text-[#85867c] hidden sm:inline">Workspace</span><span className="text-[#85867c] opacity-50 hidden sm:inline">/</span>
        <span className="font-bold text-[#20211d] truncate" data-testid="topbar-crumb">{label}</span>
      </nav>
      <div className="flex items-center gap-3">
        <ViewSwitch />
        <WorkspaceSearch headers={headers} />
        <button type="button" onClick={() => setOpen(true)} className="bg-[#f36a19] text-[#171912] text-xs font-bold px-4 py-3 rounded-[5px] inline-flex items-center gap-1.5 hover:brightness-105 hover:-translate-y-px transition-[filter,transform]" data-testid="topbar-add-new-clean-btn">
          <Plus className="h-3.5 w-3.5" /> Add New Clean
        </button>
      </div>
      <AddNewCleanDialog open={open} onOpenChange={setOpen} headers={headers} />
    </div>
  );
};

const AdminShell = ({ children }) => {
  const leftPad = useSidebarLeftOffset();
  const { pathname } = useLocation();
  const section = sectionFor(pathname);
  const heading = headingFor(pathname);
  return (
    <div className="gg-ws min-h-screen bg-[#f4f2ed]" data-testid="admin-shell">
      <AdminSidebar />
      <div data-testid="admin-shell-content" data-ws-heading={heading ? '1' : undefined} className="gg-ws-content min-h-screen transition-[padding] duration-200 ease-out" style={{ paddingLeft: `${leftPad}px` }}>
        <TopBar />
        {heading && <WorkspaceHeading eyebrow={heading[1]} title={heading[2]} subtitle={heading[3]} />}
        {section && <AdminSubNav section={section[0]} items={section[1]} sticky={false} />}
        {children}
      </div>
      <PwaInstallBanner />
    </div>
  );
};

export default AdminShell;
